/* ================================================================
   home/shared.js — 10개 클론 랜딩 변형이 공유하는 데이터 훅/헬퍼.

   각 변형(Home01~Home10)은 레이아웃·카피·팔레트를 전부 자기 것으로
   가지되, 실제 상품 데이터 배선(스토어/렌탈 API)과 링크 규칙만
   여기서 가져다 쓴다. homepage-config(관리자 히어로)는 10개 사이트가
   같은 DB 원본을 공유하므로 변형에서는 사용하지 않는다(카피 중복 방지).
   ================================================================ */
import { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { BRAND } from "../../config/brand";

export { BRAND };
export const PHONE = BRAND.phone || "1600-7542";

/* ── 테마 번호(=사이트 번호). index.html 토큰 주입, dev는 ?theme=N ── */
export function themeNumber() {
  let n = NaN;
  try {
    n = parseInt(document.documentElement.getAttribute("data-clone-theme"), 10);
    if (!(n >= 1 && n <= 10)) {
      n = parseInt(new URLSearchParams(window.location.search).get("theme"), 10);
    }
  } catch (_e) { /* noop */ }
  return n >= 1 && n <= 10 ? n : 0;
}

/* 밝은 히어로를 쓰는 변형 — Navbar가 홈 최상단에서 어두운 글자로 전환.
   2026-07-06 페이퍼 리디자인: 2·6도 라이트 페이퍼 히어로로 전환(3·8·10은 다크 잉크 유지) */
export const LIGHT_HERO_THEMES = new Set([1, 2, 4, 5, 6, 7, 9]);

/* ── 링크/이미지/가격 헬퍼 ── */
export const CAT_IMG = (name) => `/api/category-images/${encodeURIComponent(name)}`;

export const normalizeInternalLink = (link, fallback = "/store") => {
  if (!link) return fallback;
  return link.startsWith("/products") ? link.replace(/^\/products/, "/store") : link;
};

export const toNumber = (v) => Number(String(v || 0).replace(/,/g, ""));
export const money = (v) => toNumber(v).toLocaleString();

export const productTitle = (p) => p.model_name || p.name || p.title || "제품명 미등록";
export const productBrand = (p) => p.brand || p.category || p.store_category || BRAND.name;
export const productLink = (p, fallback = "/store") =>
  p.product_url || (p.slug ? `/p/${p.slug}` : fallback);
export const isExternalLink = (href) => /^https?:\/\//i.test(href || "");
export const productPrice = (p) =>
  toNumber(p.rental_price) > 0
    ? `월 ${money(p.rental_price)}원~`
    : toNumber(p.price) > 0
    ? `${money(p.price)}원`
    : "가격 문의";

/* ── 카테고리(실제 /store/:slug 라우트와 서버 이미지) ── */
export const FALLBACK_CATS = [
  { label: "고속인쇄기",     sub: "High-speed", img: "1.고속인쇄기.webp",   to: "/store/high-speed-printer" },
  { label: "디자인용 복합기", sub: "Design",     img: "2.대형 복합기.webp",  to: "/store/design-copier" },
  { label: "사무용 복합기",   sub: "Office",     img: "2.복합기.webp",       to: "/store/office-copier" },
  { label: "소형 복합기",     sub: "Compact",    img: "3.소형복합기.webp",   to: "/store/compact-copier" },
  { label: "프린터",         sub: "Printer",    img: "4.프린터.webp",       to: "/store/printer" },
  /* 토너 타일은 서버 이미지에 "Ikoffice" 로고가 박혀 있어(브랜드 누출) 중립 SVG 사용 */
  { label: "토너 · 소모품",   sub: "Toner",      img: "/cat-toner.svg",      to: "/store/toner-supplies" },
];
export const catImgSrc = (img) =>
  img && (img.startsWith("/") || /^https?:\/\//i.test(img)) ? img : CAT_IMG(img);

/* ── 상품 데이터 훅 (기존 HomePage 배선과 동일) ── */
const isToner = (p) => /토너|toner|드럼|drum|카트리지|cartridge|소모품/i.test(p.name || "");
const isRentalProduct = (p) =>
  p.sale_type === "rental" || p.sale_type === "both" || toNumber(p.rental_price) > 0;

/* ── 로딩 안정화(2026-07-06) ──
   백엔드/DB가 재시작 중이거나 순간 오류일 때 상품 섹션이 영영 비는 문제:
   ① 요청별 15s 타임아웃 + 지수 백오프 3회 재시도
   ② 성공 응답을 localStorage에 보관(7일) — 마운트 시 즉시 이전 데이터로
      그리고(fast paint), 새 응답 오면 교체. 전부 실패해도 마지막 정상
      데이터가 남아 랜딩이 절대 빈 화면이 되지 않는다.
   (nginx 쪽 stale 캐시와 이중 방어 — 둘 중 하나만 살아도 상품이 뜬다.) */
const HOME_CACHE_VER = "v1";
const HOME_CACHE_TTL = 7 * 24 * 60 * 60 * 1000;
const cacheKey = (url) => `ikhome:${HOME_CACHE_VER}:${url}`;

const readCache = (url) => {
  try {
    const raw = localStorage.getItem(cacheKey(url));
    if (!raw) return null;
    const { t, d } = JSON.parse(raw);
    if (!t || Date.now() - t > HOME_CACHE_TTL) return null;
    return d;
  } catch (_e) {
    return null;
  }
};
const writeCache = (url, d) => {
  try {
    localStorage.setItem(cacheKey(url), JSON.stringify({ t: Date.now(), d }));
  } catch (_e) { /* quota/사파리 프라이빗 등 — 캐시는 최선노력 */ }
};

async function getWithRetry(url, tries = 3) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      const { data } = await api.get(url, { timeout: 15000 });
      return data;
    } catch (e) {
      lastErr = e;
      if (i < tries - 1) await new Promise((r) => setTimeout(r, 700 * 2 ** i));
    }
  }
  throw lastErr;
}

/* url을 재시도로 가져와 normalize 후 set — 실패 시 캐시로 폴백 */
function loadList(url, normalize, set, alive, settle) {
  const cached = readCache(url);
  if (cached) set(normalize(cached));
  getWithRetry(url)
    .then((data) => {
      if (!alive.current) return;
      set(normalize(data));
      writeCache(url, data);
    })
    .catch(() => { /* 재시도 소진 — 캐시(있다면)가 이미 표시됨 */ })
    .finally(() => settle && settle());
}

export function useHomeData() {
  const [storeProducts, setStoreProducts] = useState([]);
  const [sktecProducts, setSktecProducts] = useState([]);
  const [ikProducts, setIkProducts] = useState([]);
  /* 첫 응답(또는 재시도 소진) 전까지 true — 변형은
     `loading && allProducts.length === 0`일 때 스켈레톤을 그린다 */
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const alive = { current: true };
    let pending = 3;
    const settle = () => {
      pending -= 1;
      if (pending <= 0 && alive.current) setLoading(false);
    };
    loadList(
      "/api/store/products?include_rental=true",
      (data) => {
        const p = (data && data.products) || data || [];
        return Array.isArray(p) ? p.slice(0, 48) : [];
      },
      setStoreProducts,
      alive,
      settle
    );
    loadList(
      "/api/sktec-products",
      (data) => (Array.isArray(data) ? data.slice(0, 24) : []),
      setSktecProducts,
      alive,
      settle
    );
    loadList(
      "/api/ikoffices-products",
      (data) => (Array.isArray(data) ? data.slice(0, 24) : []),
      setIkProducts,
      alive,
      settle
    );
    return () => { alive.current = false; };
  }, []);

  const allProducts = useMemo(
    () => [
      ...storeProducts.map((p) => ({ ...p, _src: "store" })),
      ...sktecProducts.map((p) => ({ ...p, _src: "sktec" })),
      ...ikProducts.map((p) => ({ ...p, _src: "ikoffices" })),
    ],
    [storeProducts, sktecProducts, ikProducts]
  );

  const rentalProducts = useMemo(
    () => allProducts.filter((p) => !isToner(p) && isRentalProduct(p)).slice(0, 12),
    [allProducts]
  );
  const saleProducts = useMemo(
    () => allProducts.filter((p) => !isRentalProduct(p) && p.sale_type !== "rental").slice(0, 12),
    [allProducts]
  );
  const tonerProducts = useMemo(
    () => allProducts.filter(isToner).slice(0, 12),
    [allProducts]
  );

  return { allProducts, rentalProducts, saleProducts, tonerProducts, loading };
}
